<template>
  <form
    v-on:submit.prevent="onSubmit"
    class="form-add">
    <input-checkbox
      v-if="hasCreatePatients && !patientId"
      title="Новый пациент"
      v-model="needsCreatingPatient" />
    <div
      class="form-add__row extra"
      v-if="needsCreatingPatient">
      <input-text
        required="required"
        title="Фамилия"
        v-bind:violations="surnameInputErrors"
        v-model="surname"
        v-letter-capital />
      <input-text
        required="required"
        title="Имя"
        v-bind:violations="nameInputErrors"
        v-model="name"
        v-letter-capital />
      <input-text
        title="Отчество"
        v-bind:violations="patronymicInputErrors"
        v-model="patronymic"
        v-letter-capital />
    </div>
    <div
      class="form-add__row extra"
      v-if="needsCreatingPatient">
      <input-typed-phone
        v-if="hasViewPatientPhone"
        v-bind:violations="phoneInputErrors"
        v-bind:value="phone.value"
        v-bind:type="phone.type"
        v-on:input="onInputPhone($event)" />
      <input-date
        label="Дата рождения"
        v-bind:violations="birthDateInputErrors"
        v-model="birthAt" />
    </div>
    <div
      class="form-add__row extra"
      v-if="needsCreatingPatient">
      <input-checkbox
        title="Согласен на рассылку"
        v-model="agreeMailing" />
    </div>
    <autocomplete-input
      v-if="!needsCreatingPatient"
      attach
      class="appointment-creation__patient multiline"
      hide-details
      item-text="text"
      item-value="id"
      label="ФИО пациента"
      required="required"
      v-bind:disable-search="true"
      v-bind:loading="isLoadingPatientList"
      v-bind:search-input.sync="patientSearchQuery"
      v-bind:violations="patientInputErrors"
      v-bind:items="patientsList"
      v-bind:value="patientId"
      v-on:input="onInputPatient"
    />
    <div>
      <div class="form-add__date__title">Желаемая дата</div>
      <div class="form-add__row">
        <date-picker-dialog
          v-model="date"
          v-bind:allowed-dates="allowedDates"
          v-bind:range="isDateRange"
          v-on:change-month="onChangeStartsMonth">
          <template v-slot:trigger="{ listenerObject }">
            <div
              v-on="listenerObject"
              class="form-add__date">
              <div class="form-add__date__text">{{dateTitle}}</div>
              <div class="form-add__date__icon" />
            </div>
          </template>
        </date-picker-dialog>
        <div class="form-add__switch">
          <button
            class="form-add__switch__option"
            type="button"
            v-bind:class="{ active: isDateRange }"
            v-on:click="setDateRange(true)">От — До</button>
          <button
            class="form-add__switch__option"
            type="button"
            v-bind:class="{ active: !isDateRange }"
            v-on:click="setDateRange(false)">Конкретная</button>
        </div>
      </div>
      <div
        v-for="(error, index) in dateInputErrors"
        v-bind:key="index"
        class="form-add__validate-error">{{ error }}</div>
    </div>
    <div>
      <div class="form-add__date__title">Желаемое время</div>
      <form-time-range
        v-bind:initial-from="timeStarts"
        v-bind:initial-to="timeEnds"
        v-bind:timeline="timeline"
        v-bind:violations="timeInputErrors"
        v-on:submit="onSubmitTime"
        button-clean />
    </div>
    <v-autocomplete
      attach
      clearable
      label="Врач"
      item-text="text"
      item-value="id"
      v-bind:error-messages="employeeInputErrors"
      v-bind:items="employees"
      v-model="employeeId">
      <template v-slot:item="{ item }">
        <item-employee v-bind:name="item.name" v-bind:positions="item.positions" />
      </template>
    </v-autocomplete>
    <v-autocomplete
      attach
      clearable
      item-text="title"
      item-value="value"
      label="Услуга"
      multiple
      v-bind:error-messages="serviceInputErrors"
      v-bind:items="services"
      v-bind:search-input.sync="searchServiceQuery"
      v-model="serviceIds"
      v-on:change="clearSearchQuery"
      v-on:update:search-input="onUpdateServiceQuery">
      <template v-slot:no-data>
        <p style="padding: 10px; margin:0;">Ничего не найдено</p>
      </template>
    </v-autocomplete>
    <textarea-length-limitation
      v-model="comment"
      placeholder="Комментарий"
      rows="2"
      v-bind:max-length="1000"
      v-bind:violations="commentInputErrors" />
    <div v-if="patientId" class="form-add__link">
      <router-link v-bind:to="{ name: 'ClinicPanelPatientBasic', params: { id: patientId } }">
        Перейти в карту пациента
      </router-link>
    </div>
    <div class="form-add__buttons">
      <button
        class="form-add__buttons__button-cancel"
        type="button"
        v-on:click="onCancel">Отмена</button>
      <button
        class="form-add__buttons__button-submit"
        v-bind:disabled="isAwaitWaitingRequest"
        type="submit">{{ submitButtonTitle }}</button>
    </div>
  </form>
</template>

<script>
import {
  Waiting,
  WaitingExistPatientRequest,
  WaitingNewPatientRequest,
} from '@/storage/gateways/WaitingListGateway';
import dateMixin, { getISODateStringByDate } from '@/mixins/dateMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import searchServiceMixin from '@/mixins/searchServiceMixin';
import { ServiceItem } from '@/storage/gateways/ServiceGateway';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputDate from '@/components/InputDate.vue';
import InputTypedPhone from '@/components/InputTypedPhone.vue';
import InputText from '@/components/InputText.vue';
import ItemEmployee from '@/components/ItemEmployee.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import FormTimeRange from '@/components/FormTimeRange.vue';
import searchPatientMixin from '@/mixins/searchPatientMixin';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

const NAME_EVENT_FETCH_WAITING_PATIENT = 'check-waiting-patient';

export default {
  components: {
    AutocompleteInput,
    DatePickerDialog,
    InputCheckbox,
    InputDate,
    InputTypedPhone,
    InputText,
    ItemEmployee,
    TextareaLengthLimitation,
    FormTimeRange,
  },
  async created() {
    this.fetchEmployeeList();
  },
  mounted() {
    if (this.patientId) {
      this.patientSearchQuery = this.patientFullName;
    }
    this.fetchPatientList(this.patientSearchQuery);
  },
  computed: {
    birthDateInputErrors: resolveInputErrors('birth-date'),
    commentInputErrors: resolveInputErrors('comment'),
    dateInputErrors: resolveInputErrors('date'),
    employeeInputErrors: resolveInputErrors('employee'),
    nameInputErrors: resolveInputErrors('name'),
    patientInputErrors: resolveInputErrors('patient'),
    patronymicInputErrors: resolveInputErrors('patronymic'),
    phoneInputErrors: resolveInputErrors('phone'),
    serviceInputErrors: resolveInputErrors('service'),
    surnameInputErrors: resolveInputErrors('surname'),
    timeInputErrors: resolveInputErrors('time'),
    /**
     * @description Имеется выбранная дата.
     * @type {boolean}
     */
    hasPickedDate() {
      if (this.isDateRange) {
        return this.selectedDateRange.length === 2;
      }
      return true;
    },
    /**
     * @description Текст кнопки отправки запроса.
     * @returns {string}
     */
    submitButtonTitle: ({ isEdit }) => (isEdit ? 'Сохранить' : 'Добавить в лист ожидания'),
    /**
     * @description Дата.
     * @type {Date|Date[]}
     */
    date: {
      get() {
        if (this.isDateRange) {
          return this.selectedDateRange;
        }
        return this.dateStarts;
      },
      set(value) {
        if (this.isDateRange) {
          const dates = value.sort((d1, d2) => (
            this.formatDateToISO(d1) > this.formatDateToISO(d2) ? 1 : 0
          ));
          this.selectedDateRange = dates;
          if (dates.length === 1) {
            [this.dateStarts] = dates;
          } else if (dates.length === 2) {
            [this.dateStarts, this.dateEnds] = dates;
          }
        } else {
          this.dateStarts = value;
        }
      },
    },
    /**
     * @description Дата в формате ISO.
     * @type {from:string,to:string}
     */
    isoDate() {
      if (this.isDateRange) {
        const dates = this.date.map(this.formatDateToISO);
        if (dates.length === 2) {
          return { from: dates[0], to: dates[1] };
        }
        const [date] = dates;
        return { from: date, to: date };
      }
      const day = this.formatDateToISO(this.date);
      return { from: day, to: day };
    },
    /**
     * @description Значение поля дата.
     * @type {string}
     */
    dateTitle({ isDateRange, date, getShortDate }) {
      const formatDate = (value) => getShortDate(value);
      if (isDateRange) {
        return date.map(formatDate).join('  —  ');
      }
      return formatDate(date);
    },
    /**
     * @description Форма открыта на редактирование.
     * @returns {boolean}
     */
    isEdit: ({ waitingId }) => waitingId !== null,
    /**
     * @description Идентификатор записи.
     * @returns {?number}
     */
    waitingId: ({ waiting }) => waiting.id,
    /**
     * @description Пациенты.
     * @returns {{id:number,text:string}[]}
     */
    patients: ({ patientList, isEdit, waiting }) => {
      if (isEdit) {
        const patient = {
          id: waiting.patientId,
          text: `${waiting.patientName} ${waiting.patientPhone}`,
        };
        return [patient, ...patientList];
      }
      return patientList;
    },
    /**
     * @description Сотрудники.
     * @returns {any}
     */
    employees: ({ employeeList, isEdit, waiting }) => {
      if (isEdit && waiting.employeeId) {
        const employee = {
          id: waiting.employeeId,
          name: waiting.employeeName,
          positions: waiting.employeePositions,
          text: `${waiting.employeeName} ${waiting.employeePositions}`,
        };
        return [employee, ...employeeList];
      }
      return employeeList;
    },
    initialServices: ({ waiting }) => (waiting.services || [])
      .map((item) => new ServiceItem(item.id, item.name)),
    selectedServices({ serviceList, initialServices, serviceIds }) {
      return serviceIds.reduce((list, id) => {
        let service = serviceList.find((item) => item.value === id);
        if (service) {
          list.push(service);
        } else {
          service = initialServices.find((item) => item.value === id);
          if (service) {
            list.push(service);
          }
        }
        return list;
      }, []);
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      birthAt: vm.birthAt,
      comment: vm.comment,
      dateEnds: vm.dateEnds,
      dateStarts: vm.dateStarts,
      employeeId: vm.employeeId,
      name: vm.name,
      patientId: vm.patientId,
      patronymic: vm.patronymic,
      phone: vm.phone,
      serviceIds: vm.serviceIds,
      surname: vm.surname,
      timeEnds: vm.timeEnds,
      timeStarts: vm.timeStarts,
    }),
  },
  data() {
    const isDateRange = this.waiting.id !== null
      ? this.waiting.dateEnds.toDateString() !== this.waiting.dateStarts.toDateString()
      : true;
    let currentDate = new Date();
    const deadLine = getISODateStringByDate(currentDate);
    currentDate = new Date(deadLine);
    return {
      /**
       * @description Текущая дата.
       * @type {Date}
       */
      currentDate,
      /**
       * @description Полное ФИО пациента.
       * @type {string}
       */
      patientFullName: this.waiting.patientFullName,
      /**
       * @description Фильтруемый список пациентов
       * @type {array}
       */
      patientsList: this.patientList,
      /**
       * @description Дата рождения пациента.
       * @type {string}
       */
      birthAt: '',
      /**
       * @description Комментарий.
       * @type {string}
       */
      comment: this.waiting.comment,
      /**
       * @description Дата окончания.
       * @type {Date}
       */
      dateEnds: this.waiting.dateEnds,
      /**
       * @description Дата начала.
       * @type {Date}
       */
      dateStarts: this.waiting.dateStarts,
      /**
       * @description Идентификатор сотрудника.
       * @type {?number}
       */
      employeeId: this.waiting.employeeId,
      /**
       * @description Список сотрудников доступных для записи.
       * @type {any[]}
       */
      employeeList: [],
      /**
       * @description Доступные даты.
       * @type {string[]}
       */
      endsAllowedDates: [],
      /**
       * @description Является периодом.
       * @type {boolean}
       */
      isDateRange,
      /**
       * @description Имя пациента.
       * @type {string}
       */
      name: '',
      /**
       * @description Новый пациент.
       * @type {boolean}
       */
      needsCreatingPatient: false,
      /**
       * @description Согласен на рассылку.
       * @type {boolean}
       */
      agreeMailing: true,
      /**
       * @description Идентификатор пациента.
       * @type {number}
       */
      patientId: this.waiting.patientId,
      /**
       * @description Отчество пациента.
       * @type {string}
       */
      patronymic: '',
      /**
       * @description Телефон пациента.
       * @type {Object}
       */
      phone: '',
      /**
       * @description Выбранный отрезок дат.
       * @type {Date[]}
       */
      selectedDateRange: [this.waiting.dateStarts, this.waiting.dateEnds],
      /**
       * @description Идентификатор услуги.
       * @type {?Array}
       */
      serviceIds: this.getServiceIdsFromServices(this.waiting.services),
      /**
       * @description Доступные даты для записи.
       * @type {string[]}
       */
      startsAllowedDates: [],
      /**
       * @description Отчество пациента.
       * @type {string}
       */
      surname: '',
      /**
       * @description Время окончания.
       * @type {string}
       */
      timeEnds: this.waiting.timeEnds,
      /**
       * @description Время начала.
       * @type {string}
       */
      timeStarts: this.waiting.timeStarts,
      /**
       * @description Временная шкала.
       * @type {any[]}
       */
      timeline: [],
      /**
       * @description Дата визита.
       * @type {Date}
       */
      dateAt: new Date(),
      /**
       * @description Запрос на поиск пациентов.
       * @type {string}
       */
      patientSearchQuery: '',
    };
  },
  methods: {
    /**
     * @description Функция описывающая условие доступности выбора дат в datepicker
     * @param {String} candidate Строка с датой в формате Y-m-d
     * @return {boolean}
     */
    allowedDates(candidate) {
      return (new Date(candidate)) >= this.currentDate
        && this.startsAllowedDates.includes(candidate);
    },
    /**
     * @description Обработать изменение пациента.
     * @param {?number} value
     * @returns {void}
     */
    onInputPatient(value) {
      this.patientId = value;
      this.onFetchNotification(value);
      if (value && this.isPatientFieldInvalid) {
        this.isPatientFieldInvalid = false;
      }
    },
    /**
     * Запросить уведомления
     *
     * @param {int} patientId
     * @returns {void}
     */
    onFetchNotification(patientId) {
      this.$emit(NAME_EVENT_FETCH_WAITING_PATIENT, patientId);
    },
    /**
     * @description получить список пациентов, с возможностью фильтра
     * @param {string} value
     * @return {void}
     */
    fetchPatientList(value = '') {
      this.$appointmentScheduleGateway.getPatientListFromAppointment(value)
        .then((patients) => {
          this.patientsList = patients.val;
        });
    },
    async fetchTimeline(date) {
      const dateISO = this.formatDateToISO(date);
      const response = await this.$appointmentScheduleGateway.viewTimeLine(
        dateISO,
        0,
        false,
      );
      response.fold(
        (error) => {
          this.notifyFail(error.title);
        },
        (result) => {
          const timeline = result;
          const addTime = (time) => {
            const slotIndex = timeline.findIndex((item) => item.title >= time);
            const slot = timeline[slotIndex];
            if (slot && slot.title !== time) {
              const item = { datetime: `${dateISO} ${time}:00`, title: time };
              timeline.splice(slotIndex, 0, item);
            }
          };
          if (this.timeStarts) {
            addTime(this.timeStarts);
          }
          if (this.timeEnds) {
            addTime(this.timeEnds);
          }
          this.timeline = timeline;
        },
      );
    },
    /**
     * Получить массив идентификаторов услуг из листа ожидания
     * @param {Array} services
     * @return {Array}
     */
    getServiceIdsFromServices(services) {
      return services.map((service) => service.id);
    },
    /**
     * @description Создать запрос с существующим пациентом.
     * @returns {WaitingExistPatientRequest}
     */
    createExistsPatientRequest() {
      return new WaitingExistPatientRequest(
        this.comment,
        this.isoDate.to,
        this.isoDate.from,
        this.employeeId,
        this.patientId,
        this.serviceIds,
        this.timeEnds,
        this.timeStarts,
      );
    },
    /**
     * @description Создать запрос с новым пациентом.
     * @returns {WaitingNewPatientRequest}
     */
    createNewPatientRequest() {
      const birthAt = this.formatDateToISO(this.birthAt);
      return new WaitingNewPatientRequest(
        birthAt,
        this.comment,
        this.isoDate.to,
        this.isoDate.from,
        this.employeeId,
        this.name,
        this.patronymic,
        this.phone,
        this.serviceIds,
        this.surname,
        this.timeEnds,
        this.timeStarts,
        this.agreeMailing,
      );
    },
    /**
     * @description Отправить событие создания записи с новым пациентом в листе ожидания.
     * @returns {void}
     */
    dispatchSubmitCreating() {
      const request = this.createNewPatientRequest();
      this.$emit('submit-new', request);
    },
    /**
     * @description Отправить событие создания записи с существующим пациентом в листе ожидания.
     * @returns {void}
     */
    dispatchSubmitEditing() {
      const request = this.createExistsPatientRequest();
      this.$emit('submit-exist', request);
    },
    /**
     * @description Получить список рабочих дней клиники.
     * @param {string} yearAndMonth
     * @returns {Promise<string[]>}
     */
    async fetchClinicWorkDays(yearAndMonth = 'now') {
      const days = await this.$appointmentScheduleGateway.viewClinicWorkDays(true, yearAndMonth);
      return days.fold([])((value) => value);
    },
    /**
     * @description Получить список сотрудников доступных для записи.
     * @returns {void}
     */
    async fetchEmployeeList() {
      const onResponse = (result) => {
        result.forEach((list) => { this.employeeList = list; });
      };
      this
        .$appointmentScheduleGateway
        .viewWorkingEmployeeList(this.isoDate.from, 'day', false, true)
        .then(onResponse);
    },
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onCancel() {
      this.$emit('cancel');
    },
    /**
     * @description Обработать изменение месяца даты окончания.
     * @param {string} yearAndMonth
     */
    async onChangeEndsMonth(yearAndMonth) {
      const dates = await this.onChangeMonth(yearAndMonth);
      this.endsAllowedDates = dates;
    },
    /**
     * @description Обработать изменение месяца.
     * @type {string} yearAndMonth Год и месяц.
     * @returns {string[]}
     */
    async onChangeMonth(yearAndMonth) {
      return this.fetchClinicWorkDays(yearAndMonth);
    },
    /**
     * @description Обработать изменение месяца даты начала.
     * @param {string} yearAndMonth
     */
    async onChangeStartsMonth(yearAndMonth) {
      const dates = await this.onChangeMonth(yearAndMonth);
      this.startsAllowedDates = dates;
    },
    /**
     * @description Обработать отправку данных.
     * @returns {void}
     */
    onSubmit() {
      if (this.needsCreatingPatient) {
        this.dispatchSubmitCreating();
      } else {
        this.dispatchSubmitEditing();
      }
    },
    /**
     * @description Установить период.
     * @param {boolean} value
     * @returns {void}
     */
    setDateRange(value) {
      this.isDateRange = value;
    },
    /**
     * @description Обработать установку времени.
     * @param {string}   timeStarts   Время начала.
     * @param {string}   timeEnds     Время окончания.
     * @returns {void}
     */
    onSubmitTime(timeStarts, timeEnds) {
      this.timeStarts = timeStarts;
      this.timeEnds = timeEnds;
    },
    /**
     * @description Обработать ввод телефона.
     * @param {any}    newValue
     * @returns {void}
     */
    onInputPhone(newValue) {
      this.phone = newValue;
    },
    /**
     * @description Обработать обновление запроса на поиск пациентов.
     * @param {string|null} value
     * @returns {void}
     */
    onUpdatePatientSearchQuery(value) {
      this.$emit('update-patient-search-query', value);
    },
  },
  props: {
    /**
     * @description Полное ФИО пациента.
     */
    initialPatientFullName: { default: () => '', type: String },
    /**
     * @description Может создавать пациентов.
     * @type {boolean}
     */
    hasCreatePatients: { default: () => false, type: Boolean },
    /**
     * @description Может просматривать номер пациента.
     * @type {boolean}
     */
    hasViewPatientPhone: { required: true, type: Boolean },
    /**
     * @description Ограничения нарушений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
    /**
     * @description Список пациентов.
     * @type {any[]}
     */
    patientList: { type: Array, required: true },
    /**
     * @description Запись.
     * @type {Waiting}
     */
    waiting: { type: Waiting, required: true },
    /**
     * @description Ожидание завершения запроса.
     * @type {boolean}
     */
    isAwaitWaitingRequest: { type: Boolean, default: false },
    /**
     * @description Пациенты загружаются.
     * @type {boolean}
     */
    isLoadingPatientList: { type: Boolean, default: false },
  },
  mixins: [
    searchPatientMixin,
    dateMixin,
    formMeaningfulFieldsMixin,
    searchServiceMixin,
  ],
  name: 'FormAddToWaitingList',
  watch: {
    async employeeId() {
      const getYearAndMonth = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${year}-${month}`;
      };
      if (this.hasPickedDate) {
        this.onChangeStartsMonth(getYearAndMonth(this.dateStarts));
        this.onChangeEndsMonth(getYearAndMonth(this.dateEnds));
      }
    },
    patientId(id) {
      const patient = this.patients.find((item) => item.id === id);
      if (patient) {
        this.phone = patient.phone;
      }
    },
    dateAt: {
      immediate: true,
      handler: 'fetchTimeline',
    },
    needsCreatingPatient() {
      if (this.needsCreatingPatient) {
        this.phone = { type: 'mobile', value: '' };
      }
    },
    patientSearchQuery: {
      handler(newValue, oldValue) {
        if (newValue || newValue === '') {
          const query = this.prepareQuery(newValue, oldValue);
          if (query === null) {
            return;
          }
          this.fetchPatientList(query);
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.form-add {
  flex-direction: column;
  display: flex;
  gap: 25px;
  margin-top: 10px;
  &__datetime {
    display: grid;
    grid-template-columns: repeat(2, fit-content(250px));
    column-gap: 20px;
    &__value {
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      color: var(--action-color);
      border-bottom: 1px dashed;
      cursor: pointer;
      &:hover {
        border-bottom: 1px dashed transparent;
      }
    }
    &__error {
      font-size: 12px;
      color: var(--warning-color);
    }
  }
  &__multiline {
    &::v-deep .v-list-item__title {
      white-space: pre-line;
    }
  }
  &__row {
    display: flex;
    flex-direction: row;
    gap: 22px;
    align-items: center;
    &.extra {
      align-items: flex-start;
      gap: 10px;
    }
  }
  &__date {
    align-items: center;
    background-color: white;
    border-radius: 4px;
    border: 1px solid var(--border-color);
    color: var(--reading-color);
    display: flex;
    flex-direction: row;
    font-size: 16px;
    font-weight: 400;
    height: 40px;
    justify-content: center;
    line-height: 19px;
    padding: 0 0 0 10px;
    position: relative;
    text-align: center;
    width: 244px;
    &__title {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90a0b7;
      margin-bottom: 3px;
    }
    &__icon {
      // position: absolute;
      right: 1px !important;
      top: 1px !important;
      background-repeat: no-repeat;
      background-image: url("@/assets/img/calendar.svg");
      background-position: center;
      background-color: #eaeff3;
      border-radius: 0px 3px 3px 0px;
      width: 36px;
      height: 36px;
    }
    &__text {
      color: var(--reading-color);
      flex: 1;
      font-size: 16px;
      font-weight: 400;
      line-height: 19px;
      white-space: break-spaces;
      text-align: left;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 214px 312px;
    grid-gap: 10px;
    margin: 20px 0 0 auto;
    &__button-cancel {
      @include buttons.btn-transparent(100%, 56px, 20px);
    }
    &__button-submit {
      @include buttons.btn-blue(100%, 56px, 20px);
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
  }
  &__switch {
    width: fit-content;
    height: fit-content;
    display: grid;
    grid-template-columns: 89px 111px;
    border-radius: 4px;
    overflow: hidden;
    align-items: center;
    &__option {
      background-color: #eaeff3;
      color: var(--reading-color);
      cursor: pointer;
      font-size: 14px;
      font-weight: 400;
      line-height: 17px;
      padding: 8px 0;
      text-align: center;
      transition: background-color var(--animate-duration);
      &.active {
        background-color: var(--action-color);
        color: white;
      }
    }
  }
  &__time {
    display: grid;
    grid-template-columns: auto fit-content(10px) auto;
    column-gap: 7px;
    align-items: center;
    time {
      text-align: left;
    }
    .single {
      grid-column: 1/4;
      text-align: left;
      width: fit-content;
      color: var(--action-color);
      border-bottom: 1px dotted transparent;
      &:hover {
        border-bottom: 1px dotted var(--action-color);
      }
    }
    &__row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
    }
  }
  &__validate-error {
    color: red;
    font-size: 0.8rem;
  }
  &__link {
    margin: -25px 0 0 0;
    a:hover {
      text-decoration: none;
    }
  }
}
@media screen and (max-width: 620px) {
  .form-add {
    &__row {
      flex-flow: row wrap;
    }
    &__buttons {
      grid-template-columns: 1fr;
      margin: 20px 0 0 0;
    }
  }
}
</style>
